import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {ProductSample, SaveInventoryItem, SaveInventoryRequestItem} from '../../utils'
import {webEvents} from '../../../common/WebEvents'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import {CachedProduct, getCachedProducts} from '../../productCache'
import {ExcludedDetailsDialog, SerialInput, StatusTooltip} from '../../../common/ui'

interface SerialStepProps {
    productSamples: ProductSample[]
    serialItems: SaveInventoryItem[]
    saveRequestItem: (requestItem: SaveInventoryRequestItem) => void
}

const SerialStep = ({
                        productSamples,
                        serialItems,
                        saveRequestItem
                    }: SerialStepProps) => {
    const [currentSerial, setCurrentSerial] = useState<string>('')
    const [selectedIndex, setSelectedIndex] = useState<number>(0)
    const [open, setOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState<SaveInventoryItem | null>(null)
    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [savedQty, setSavedQty] = useState<number | null>(null)
    const [savedExcludedQty, setSavedExcludedQty] = useState<number | null>(0)
    const [pendingQty, setPendingQty] = useState<number | null>(null)
    let inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (window.sessionStorage.getItem('blocking') === 'true' && inputRef.current) {
            inputRef.current.blur()
        } else if (inputRef.current) {
            inputRef.current.focus()
        }
    }, [inputRef, window.sessionStorage.getItem('blocking')])

    useEffect(() => {
        const fetchProducts = async () => {
            const cachedProducts = await getCachedProducts()
            setProducts(Object.values(cachedProducts))
        }

        fetchProducts()
    }, [])

    const handleClickOpen = (item: SaveInventoryItem) => {
        setSelectedItem(item)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        setSelectedItem(null)
    }

    const handleCopy = (str: string) => {
        navigator.clipboard.writeText(str)
            .then(() => {
                alert('Copied to clipboard!')
            })
            .catch(err => {
                console.error('Failed to copy: ', err)
            })
    }

    const getModelNumber = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product ? product.model : 'Unknown Model'
    }

    const sampleSerialItems = useMemo(
        () => productSamples.filter(x => x.serialized),
        [productSamples]
    )

    const reportSerialItems = [...serialItems].reverse()

    useEffect(() => {
        setCurrentSerial('')
    }, [selectedIndex])

    const handleAddSerial = () => {
        // check that length is between 1 and 128
        if (currentSerial && currentSerial.length > 0 && currentSerial.length < 128) {
            saveRequestItem({
                op: 'add',
                sku: '',
                quantity: 1,
                serial: currentSerial,
                bin: null,
                enteredAt: new Date().toISOString()
            })

            setCurrentSerial('')
        } else {
            webEvents.warning('serial not valid', {serial: currentSerial})
        }
    }

    const handleRemoveSerial = (serial: string | null, enteredAt: string) => {
        saveRequestItem({
            op: 'delete',
            sku: '',
            quantity: 1,
            serial,
            bin: null,
            enteredAt
        })
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            handleAddSerial()
        }
    }

    const handlePreviousSerial = () => {
        if (selectedIndex > 0) {
            setSelectedIndex(selectedIndex - 1)
        }
    }

    const handleNextSerial = () => {
        if (selectedIndex < sampleSerialItems.length - 1) {
            setSelectedIndex(selectedIndex + 1)
        }
    }

    useEffect(() => {
        let serialQty = 0
        let serialExcludedQty = 0
        let serialPendingQty = 0

        for (let item of reportSerialItems) {
            if (item.progress) {
                serialPendingQty += item.quantity
                if (item.progress === 'q-delete' || item.progress === 'deleting' && item.excluded) {
                    serialExcludedQty += item.quantity
                } else if (item.progress === 'q-delete' || item.progress === 'deleting') {
                    serialQty += item.quantity
                }
            } else if (item.excluded) {
                serialExcludedQty += item.quantity
            } else {
                serialQty += item.quantity

            }
        }
        setSavedQty(serialQty)
        setSavedExcludedQty(serialExcludedQty)
        setPendingQty(serialPendingQty)
    }, [reportSerialItems])

    return (
        <>
            <Box display="flex" justifyContent="space-between" my={2} px={2}>
                <Button
                    onClick={handlePreviousSerial}
                    variant="contained"
                    color="primary"
                    disabled={selectedIndex <= 0}>
                    Previous {' '}
                    {selectedIndex > 0
                        ? sampleSerialItems[selectedIndex - 1].sku
                        : ''}
                </Button>
                <Button
                    onClick={handleNextSerial}
                    variant="contained"
                    color="primary"
                    disabled={selectedIndex >= sampleSerialItems.length - 1}>
                    Next {' '}
                    {selectedIndex < sampleSerialItems.length - 1
                        ? sampleSerialItems[selectedIndex + 1].sku
                        : ''}
                </Button>
            </Box>
            <Box
                flex={1}
                p={2}
                border={2}
                borderColor="grey.400"
                borderRadius={2}
                position="relative">
                <Typography variant="h5">Scan/Enter Serials</Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    <span style={{fontWeight: 700, color: '#2e7d32'}}>Saved: </span>
                    {savedQty === 1 ? `${savedQty} serial` : `${savedQty} serials`},{' '}
                    <span style={{color: 'red'}}>{savedExcludedQty} excluded </span>
                    <span
                        style={{fontWeight: 700}}>Pending: </span>
                    {pendingQty} quantity
                </Typography>
                <audio ref={audioPlayer} src={excludedSound} />
                {sampleSerialItems.length > 0 && (
                    <SerialInput
                        currentSerial={currentSerial}
                        setCurrentSerial={setCurrentSerial}
                        handleKeyDown={handleKeyDown}
                        inputRef={inputRef}
                        sampleSerialItems={sampleSerialItems}
                        selectedIndex={selectedIndex}
                    />
                )}

                <TableContainer component={Paper} className="my-3">
                    <Table size="small">
                        <TableHead>
                            <TableRow
                                style={{
                                    borderBottom:
                                        reportSerialItems.length > 0 &&
                                        reportSerialItems[0].excluded
                                            ? 'none'
                                            : '2px solid #ddd'
                                }}>
                                <TableCell sx={{display: 'none'}}></TableCell>
                                <TableCell>Item</TableCell>
                                <TableCell>Serial</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sampleSerialItems.length > 0 ? (
                                reportSerialItems.length > 0 ? (
                                    reportSerialItems
                                        .map(item => {
                                            const excluded = item.progress === 'q-delete' || item.progress === 'deleting'
                                                ? '' : item.excluded
                                            return (
                                                <TableRow
                                                    key={item.serial}
                                                    sx={{
                                                        border: item.excluded ? '2px solid red' : 'none',
                                                        color: item.excluded ? 'red' : 'inherit'
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={{display: 'none'}}></TableCell>
                                                    <TableCell>{item.sku}</TableCell>
                                                    <TableCell>{item.serial}</TableCell>
                                                    <TableCell>{getModelNumber(item.sku)}</TableCell>
                                                    <TableCell>
                                                        {excluded ? (
                                                            <Tooltip title={<h1 style={{
                                                                color: 'text.primary',
                                                                fontSize: 15,
                                                                fontWeight: 'medium'
                                                            }}>{excluded.startsWith('not-found:') ? 'not-found' : excluded}</h1>}>
                                                                <Box
                                                                    fontSize="small"
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        color: 'red'
                                                                    }}
                                                                    onClick={() => handleClickOpen(item)}>
                                                                    {excluded.split(':')[0]}
                                                                </Box>
                                                            </Tooltip>
                                                        ) : (
                                                            <StatusTooltip item={item} onClick={handleClickOpen} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            sx={{cursor: 'pointer'}}
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleRemoveSerial(
                                                                    item.serial, item.enteredAt
                                                                )
                                                            }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No saved serial items
                                        </TableCell>
                                    </TableRow>
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No serial items in sample
                                    </TableCell>
                                </TableRow>

                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {selectedItem && (
                    <ExcludedDetailsDialog
                        open={open}
                        selectedItem={selectedItem}
                        onClose={handleClose}
                        onCopy={handleCopy}
                    />
                )}
            </Box>
        </>
    )
}

export default SerialStep
